import React, { useCallback, useRef, createContext, useContext, useEffect, useState } from "react";
import { useWebSocket } from "WebSocketContext";
import { Player } from "GameContext";

import { useGameContext } from "GameContext";

type ChatContextType = {
  sendMessage: (roomId: string, message: MessageType) => void;
  createChatRoom : (roomId: string, participants: Array<number>) => void;
  chatRooms: Map<string, ChatRoomType>;
};

export type MessageType = {
  author: Player;
  msg: string;
  date: Date;
}

export type ChatRoomType = {
  roomId: string;
  participants: Array<number>;
  messages: Array<MessageType>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { playerId, socket } = useWebSocket();

  const chatRoomsRef = useRef<Map<string, ChatRoomType>>(new Map());
  const [chatRooms, setChatRooms] = useState<Map<string, ChatRoomType>>(new Map());
  const {
    hostId,
    connectedIds,
    isAdmin,
    gameId,
    gameStarted,
    publicEstateIds,
    privateEstateIds,
    players,
  } = useGameContext() as any;

  const onMessage = useCallback((event: { data: string; }) => {
    if (event.data) {
      const message = JSON.parse(event.data);

      if (message.kind === "ChatMessage") {
        const chatRoomId = message.roomId;
        const chatRoom = chatRoomsRef.current.get(chatRoomId);
        if (!chatRoom) {
          return;
        }
        chatRoomsRef.current.set(chatRoomId, {
          ...chatRoom,
          messages: [...chatRoom.messages, message.message],
        });
        setChatRooms(() => new Map(chatRoomsRef.current));
        return;
      }

      if (message.kind === "PlayerJoinMidGame") {
        // Add player to relevant chat rooms

        // TODO ROLE SPECIFIC CHAT ROOMS
        for (const [key, room] of chatRoomsRef.current) {
          if (!room.participants.includes(message.playerInfo.id)) {
            if (key === "public-estate" && message.playerInfo.estate === "public") {
              room.participants.push(message.playerInfo.id);
            }
            if (key === "private-estate" && message.playerInfo.estate === "private") {
              room.participants.push(message.playerInfo.id);
            }

            const [estate, role] = key.split("-");

            if (estate === message.playerInfo.estate && role === message.playerInfo.role) {
              room.participants.push(message.playerInfo.id);
            }

            if (key === "everyone") {
              room.participants.push(message.playerInfo.id);
            }
          }
        }

        setChatRooms(() => new Map(chatRoomsRef.current));
        if (isAdmin) {
          console.log("AM ADMIN AND SENDING NEW GUY CHAT INFO");
          const chatRoomsArray: ChatRoomType[] = [];

          for (const [, room] of chatRoomsRef.current) {
            if (room.participants.includes(message.playerInfo.id)) {
              chatRoomsArray.push(room);
            }
          }

          socket?.send(JSON.stringify({
            kind: "UpdateAllChatRooms",
            toIds: [message.playerInfo.id],
            chatRoomsArray: chatRoomsArray,
          }));
        }
      }

      if (message.kind === "UpdateAllChatRooms") {
        chatRoomsRef.current = new Map();
        for (let i=0; i < message.chatRoomsArray.length; i++) {
          const room = message.chatRoomsArray[i]
          chatRoomsRef.current.set(room.roomId, room)
        }
        setChatRooms(() => new Map(chatRoomsRef.current));
      }

      if (message.kind === "PlayerDisconnected") {
        // Remove player from chat rooms
        for (const [key, room] of chatRoomsRef.current) {
          const iOfP = room.participants.indexOf(message.playerId);
          if (iOfP >= 0) {
            room.participants.splice(iOfP, 1);
          }
        }

        setChatRooms(prev => new Map(chatRoomsRef.current))
      }

      if (message.kind === "NewChatRoom") {
        if (chatRoomsRef.current.has(message.roomId)) {
          return
        }

        chatRoomsRef.current.set(message.roomId, {
          roomId: message.roomId,
          participants: message.participants,
          messages: [],
        })

        setChatRooms(prev => new Map(chatRoomsRef.current))
        return
      }

      if (message.kind === "AddUserToChatRoom") {
        if (!chatRoomsRef.current.has(message.roomId)) {
          return
        }

        const rr = chatRoomsRef.current.get(message.roomId);
        if (!rr || rr.participants.includes(message.newUserId)) {
          return
        }

        rr.participants.push(message.newUserId)


        setChatRooms(prev => new Map(chatRoomsRef.current))
        return
      }

    }
  }, [isAdmin])

  function createChatRoom(roomId: string, participants: Array<number>) {
    if (!participants.includes(playerId) && !isAdmin) {
      return
    }

    const mes = JSON.stringify({
      kind: "NewChatRoom",
      toIds: participants.includes(hostId) ? participants : [hostId, ...participants],
      roomId: roomId,
      participants: participants
    })
    socket?.send( mes );
  }

  function sendMessage(roomId: string, message: MessageType) {
    if (chatRooms.has(roomId)) {
      const chatRoom = chatRooms.get(roomId);
      if (!chatRoom) {
        return;
      }

      const mes = JSON.stringify({
        kind: "ChatMessage",
        toIds: chatRoom.participants.includes(hostId) ? chatRoom.participants : [hostId, ...chatRoom.participants],
        roomId: roomId,
        message: message,
      });

      socket?.send( mes );
    }
  }

  useEffect(() => {
    if (!socket) return;
    socket.addEventListener("message", onMessage);

    return () => {
      if (socket) {
        socket.removeEventListener("message", onMessage);
      }
    }
  }, [socket, onMessage]);

  useEffect(() => {
    if (gameStarted) {
      createChatRoom("everyone", connectedIds);
      createChatRoom("public-estate", publicEstateIds)
      createChatRoom("private-estate", privateEstateIds)

      const availableRoleGroups: string[] = []
      for (let i=0; i < players.length ; i++) {
        const key =`${players[i].estate}-${players[i].role}`
        if (availableRoleGroups.includes(key)) {
          continue
        }

        availableRoleGroups.push(key);
      }


      for (let i = 0; i < availableRoleGroups.length; i ++) {
        const key = availableRoleGroups[i]
        const [estate, role] = key.split("-");

        const roleGroupPlayers = []
        for (let j = 0; j < players.length; j++) {
          if (players[j].estate === estate && players[j].role === role) {
            roleGroupPlayers.push(players[j].id);
          }
        }

        createChatRoom(key, roleGroupPlayers);
      }
    }
  }, [gameStarted])

  useEffect(() => {
    chatRoomsRef.current = new Map()
    setChatRooms(new Map());
  }, [gameId])



  return (
    <ChatContext.Provider value={{
      sendMessage,
      createChatRoom,
      chatRooms,
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) throw new Error("useChatContext must be used inside ChatContextProvider");
  return context;
};
